import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import useSEO from "@/hooks/useSEO";
import { BreadcrumbSchema } from "@/components/SEOStructuredData";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import SkipToContent from "@/components/SkipToContent";
import TalentCard from "@/components/TalentCard";
import { TalentTableView } from "@/components/TalentTableView";
import TalentCTA from "@/components/TalentCTA";
import ScrollReveal from "@/components/ScrollReveal";
import StaggerContainer from "@/components/StaggerContainer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutGrid, List } from "lucide-react";

// Import placeholder image
import placeholderTalent from "@/assets/placeholder-talent.jpg";

const ITEMS_PER_PAGE = 12;

const Talent = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.substring(0, 2) || "en";
  const [languageFilter, setLanguageFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  useSEO({
    title: t("talentPage.title"),
    description: t("seo.talentDescription", "Browse our roster of professional voiceover artists and on-camera talent. English and French bilingual performers across Canada."),
  });

  const breadcrumbs = [
    { name: currentLang === "fr" ? "Accueil" : "Home", url: `https://firststagetalent.com/${currentLang}` },
    { name: t("talentPage.title"), url: `https://firststagetalent.com/${currentLang}/talent` },
  ];

  const { data: talent, isLoading } = useQuery({
    queryKey: ["public-talent", currentLang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("talent")
        .select("id, name, slug, specialty, specialty_fr, languages, image_url, is_active, is_featured, display_order, bio, bio_fr")
        .eq("is_active", true)
        .order("display_order");
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Helper to get localized specialty
  const getLocalizedSpecialty = (t: { specialty: string; specialty_fr: string | null }) => {
    if (currentLang === "fr" && t.specialty_fr) {
      return t.specialty_fr;
    }
    return t.specialty;
  };

  // Helper to get localized bio
  const getLocalizedBio = (t: { bio: string | null; bio_fr: string | null }) => {
    if (currentLang === "fr" && t.bio_fr) {
      return t.bio_fr;
    }
    return t.bio || "";
  };

  // Extract unique languages from all talent
  const availableLanguages = useMemo(() => {
    if (!talent) return [];
    const languageSet = new Set<string>();
    talent.forEach((t) => {
      t.languages?.forEach((lang) => languageSet.add(lang));
    });
    // Sort alphabetically, but ensure English and French come first
    const sorted = Array.from(languageSet).sort((a, b) => {
      if (a === "English") return -1;
      if (b === "English") return 1;
      if (a === "French") return -1;
      if (b === "French") return 1;
      return a.localeCompare(b);
    });
    return sorted;
  }, [talent]);

  // Check if bilingual filter should be shown (at least one talent has both English and French)
  const showBilingualFilter = useMemo(() => {
    if (!talent) return false;
    return talent.some((t) => t.languages?.includes("English") && t.languages?.includes("French"));
  }, [talent]);

  const filteredTalent = useMemo(() => {
    if (!talent) return [];
    
    // Helper function to extract last name
    const getLastName = (name: string) => {
      const parts = name.trim().split(/\s+/);
      return parts[parts.length - 1].toLowerCase();
    };
    
    return talent
      .filter((t) => {
        if (languageFilter === "bilingual") {
          // Bilingual filter: must have both English and French
          return t.languages?.includes("English") && t.languages?.includes("French");
        }
        if (languageFilter !== "all" && !t.languages?.includes(languageFilter)) {
          return false;
        }
        return true;
      })
      .sort((a, b) => getLastName(a.name).localeCompare(getLastName(b.name)));
  }, [talent, languageFilter]);

  const visibleTalent = useMemo(() => {
    return filteredTalent.slice(0, visibleCount);
  }, [filteredTalent, visibleCount]);

  const hasMore = visibleCount < filteredTalent.length;

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
  }, []);

  // Reset visible count when filter changes
  const handleFilterChange = useCallback((filter: string) => {
    setLanguageFilter(filter);
    setVisibleCount(ITEMS_PER_PAGE);
  }, []);

  const FilterButton = ({ 
    value, 
    current, 
    onClick, 
    children 
  }: { 
    value: string; 
    current: string; 
    onClick: () => void; 
    children: React.ReactNode;
  }) => (
    <Button
      variant={current === value ? "default" : "outline"}
      size="sm"
      onClick={onClick}
      className="text-xs"
    >
      {children}
    </Button>
  );

  const getImageUrl = (t: { slug: string; image_url: string | null }) => {
    if (t.image_url && t.image_url.startsWith("http")) {
      return t.image_url;
    }
    return placeholderTalent;
  };

  return (
    <div className="min-h-screen">
      <BreadcrumbSchema items={breadcrumbs} />
      <SkipToContent />
      <Header />
      <main id="main-content" tabIndex={-1} className="outline-none">
        <PageHero 
          title={t("talentPage.title")} 
          subtitle={t("talentPage.subtitle")}
        />
        
        {/* Filters */}
        <section className="py-6 sm:py-8 border-b border-border sm:sticky sm:top-20 md:top-24 bg-background z-40">
          <div className="container-wide">
            <div className="flex flex-wrap items-end justify-between gap-4 sm:gap-6">
              {/* Language Filter */}
              <div className="space-y-2 flex-1 sm:flex-none">
                <label className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                  {t("talentPage.language")}
                </label>
                
                {/* Mobile: Dropdown */}
                <div className="sm:hidden">
                  <Select value={languageFilter} onValueChange={handleFilterChange}>
                    <SelectTrigger className="w-full min-h-[44px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("talentPage.all")}</SelectItem>
                      {availableLanguages.map((lang) => (
                        <SelectItem key={lang} value={lang}>
                          {t(`talentPage.${lang.toLowerCase()}`, lang)}
                        </SelectItem>
                      ))}
                      {showBilingualFilter && (
                        <SelectItem value="bilingual">{t("talentPage.bilingual", "Bilingual")}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Desktop: Buttons */}
                <div className="hidden sm:flex gap-2 flex-wrap">
                  <FilterButton value="all" current={languageFilter} onClick={() => handleFilterChange("all")}>
                    {t("talentPage.all")}
                  </FilterButton>
                  {availableLanguages.map((lang) => (
                    <FilterButton 
                      key={lang} 
                      value={lang} 
                      current={languageFilter} 
                      onClick={() => handleFilterChange(lang)}
                    >
                      {t(`talentPage.${lang.toLowerCase()}`, lang)}
                    </FilterButton>
                  ))}
                  {showBilingualFilter && (
                    <FilterButton value="bilingual" current={languageFilter} onClick={() => handleFilterChange("bilingual")}>
                      {t("talentPage.bilingual", "Bilingual")}
                    </FilterButton>
                  )}
                </div>
              </div>

              {/* View Toggle */}
              <div className="flex gap-1 bg-muted p-1 rounded-lg">
                <Button
                  variant={viewMode === "grid" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  className="h-8 w-8 p-0"
                  aria-label="Grid view"
                >
                  <LayoutGrid size={16} />
                </Button>
                <Button
                  variant={viewMode === "table" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("table")}
                  className="h-8 w-8 p-0"
                  aria-label="Table view"
                >
                  <List size={16} />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Talent Grid */}
        <section className="pt-6 pb-16 sm:pb-20">
          <div className="container-wide">
            {/* Instruction text */}
            <p className="text-muted-foreground text-center mb-6">
              {t("talentPage.instruction", "Select a talent to view their profile and request them for your project.")}
            </p>
            {isLoading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="space-y-4">
                    <Skeleton className="aspect-[4/5] w-full" />
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ))}
              </div>
            ) : visibleTalent.length > 0 ? (
              <>
                {viewMode === "grid" ? (
                  <StaggerContainer 
                    className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8" 
                    staggerDelay={0.05}
                    key={`grid-${languageFilter}-${visibleCount}`}
                  >
                  {visibleTalent.map((talentItem) => (
                      <TalentCard
                        key={talentItem.id}
                        id={talentItem.slug}
                        name={talentItem.name}
                        image={getImageUrl(talentItem)}
                        specialties={talentItem.languages || []}
                        voiceType={getLocalizedSpecialty(talentItem)}
                        talentDbId={talentItem.id}
                        bio={getLocalizedBio(talentItem)}
                        isActive={talentItem.is_active}
                        isFeatured={talentItem.is_featured || false}
                        displayOrder={talentItem.display_order || 0}
                      />
                    ))}
                  </StaggerContainer>
                ) : (
                  <TalentTableView 
                    talent={visibleTalent} 
                    getImageUrl={getImageUrl}
                  />
                )}
                
                {hasMore && (
                  <div className="flex justify-center mt-12">
                    <Button 
                      variant="outline" 
                      size="lg"
                      onClick={loadMore}
                    >
                      {t("talentPage.loadMore", "Load More")} ({filteredTalent.length - visibleCount} remaining)
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <ScrollReveal className="text-center py-16">
                <p className="text-muted-foreground">{t("talentPage.noTalentFound")}</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => handleFilterChange("all")}
                >
                  {t("talentPage.clearFilters")}
                </Button>
              </ScrollReveal>
            )}
          </div>
        </section>

        {/* CTA for prospective talent */}
        <TalentCTA />
      </main>
      <Footer />
    </div>
  );
};

export default Talent;
